import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowSquareOut, EnvelopeSimple, Pulse } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const HEALTH_COLORS = {
  green: { dot: 'bg-emerald-500', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  amber: { dot: 'bg-amber-500', text: 'text-amber-700', ring: 'ring-amber-200' },
  red: { dot: 'bg-red-500', text: 'text-red-700', ring: 'ring-red-200' },
};

function timeAgo(iso) {
  if (!iso) return 'nooit';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || isNaN(ms)) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min}m geleden`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}u geleden`;
  const d = Math.floor(hr / 24);
  return `${d}d geleden`;
}

/**
 * Compact health overview for all profiles the user has access to.
 * Renders one card per profile with Graph status, last sync, today's email count.
 * Click navigates to /settings/profiles for super-admins, or to /inbox otherwise.
 */
export default function ProfileHealthWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/api/profiles/health`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { profiles: [] })
      .then(d => { if (mounted) { setItems(d.profiles || []); setLoading(false); } })
      .catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || items.length === 0) return null;

  const hasIssue = items.some(i => i.health === 'red');

  return (
    <div className="mb-6" data-testid="profile-health-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Pulse size={14} weight="bold" className="text-zinc-700" />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Profielen Health</span>
          {hasIssue && (
            <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-800 px-1.5 py-0.5 rounded-sm">
              Aandacht nodig
            </span>
          )}
        </div>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(240px, 1fr))` }}>
        {items.map(p => {
          const c = HEALTH_COLORS[p.health] || HEALTH_COLORS.amber;
          const onClick = () => {
            if (p.health === 'red') navigate('/settings/profiles');
            else navigate('/inbox');
          };
          return (
            <button
              key={p.slug}
              onClick={onClick}
              data-testid={`health-card-${p.slug}`}
              className="text-left bg-white border border-zinc-200 hover:border-zinc-400 hover:shadow-sm transition-all rounded-sm p-3 group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                  <span className="font-semibold text-sm text-zinc-900 truncate">{p.name}</span>
                </div>
                <ArrowSquareOut size={11} className="text-zinc-300 group-hover:text-zinc-600 mt-0.5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ring-2 ${c.ring}`} />
                  <span className={`text-[11px] font-medium ${c.text}`} data-testid={`health-label-${p.slug}`}>{p.label}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                  <EnvelopeSimple size={11} className="flex-shrink-0" />
                  <span data-testid={`health-emails-${p.slug}`}>
                    <span className="font-mono font-medium text-zinc-700">{p.emails_today}</span> vandaag
                  </span>
                  <span className="text-zinc-300">·</span>
                  <span className="text-[10px]">sync {timeAgo(p.last_sync)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
